import {
    Box,
    CircularProgress,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    type SelectChangeEvent,
    Stack,
    Tab,
    Tabs,
    useMediaQuery,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PageWrapper from '@/components/PageWrapper';
import { useLawFirmContext } from '@/hooks/useLawFirmContext';
import { useLawFirm } from '@/queries/lawFirms';
import { useUser, useUsers } from '@/queries/users';
import useIdentityStore from '@/stores/useIdentityStore';
import { theme } from '@/theme';
import { hasOrganizationalAdminRole } from '@/utils';
import IntakeOptions from './IntakeOptions';
import LexoAdmin from './LexoAdmin';
import Logo from './Logo';
import Notification from './Notification';
import RetainerAgreement from './RetainerAgreement';
import Signature from './Signature';
import SupportAccess from './SupportAccess';

const TAB_ORDER = ['logo', 'signature', 'notification', 'intake-options', 'documents'];
const SUPER_ADMIN_TABS = ['lexo-admin'];
// Private firms only, for the firm's admins and Lexo Admins
const PRIVATE_FIRM_TABS = ['lexo-access'];

const TAB_LABELS: Record<string, string> = {
    logo: 'Logo Configuration',
    signature: 'Signature Configuration',
    notification: 'Email Notifications',
    'intake-options': 'Intake Options',
    documents: 'Legal Documents',
    'lexo-access': 'Lexo Access',
    'lexo-admin': 'Lexo Admin',
};

export default function Settings() {
    const { tab } = useParams();
    const navigate = useNavigate();

    const loggedInUser = useIdentityStore((s) => s.user);
    const { isSuperAdmin, activeLawFirmId, myLawFirmId } = useLawFirmContext();

    const [selectedUserId, setSelectedUserId] = useState<number | null>(
        loggedInUser?.userId ?? null,
    );

    const { data: lawFirm = null, isLoading: isLawFirmLoading } = useLawFirm(activeLawFirmId);

    const isOwnFirmAdmin =
        activeLawFirmId === myLawFirmId && hasOrganizationalAdminRole(loggedInUser?.roles);
    const showLexoAccess = !!lawFirm?.isPrivate && (isOwnFirmAdmin || isSuperAdmin);

    const tabOrder = useMemo(
        () => [
            ...TAB_ORDER,
            ...(showLexoAccess ? PRIVATE_FIRM_TABS : []),
            ...(isSuperAdmin ? SUPER_ADMIN_TABS : []),
        ],
        [isSuperAdmin, showLexoAccess],
    );

    const currentTab = useMemo(() => {
        const initialIndex = tabOrder.indexOf(tab ?? 'logo');
        return initialIndex === -1 ? 0 : initialIndex;
    }, [tab, tabOrder]);

    const { data: users = [], isLoading: isUsersLoading } = useUsers({
        lawFirmId: activeLawFirmId,
    });

    const { data: selectedUser = null, isLoading: isUserLoading } = useUser(selectedUserId);

    useEffect(() => {
        const isLoggedInUsersFirm = activeLawFirmId === myLawFirmId;
        setSelectedUserId(isLoggedInUsersFirm ? (loggedInUser?.userId ?? null) : null);
    }, [activeLawFirmId, myLawFirmId, loggedInUser?.userId]);

    // When users load after a law firm change, default to the logged-in user
    // (if they belong to the selected firm) or the first user in the list
    useEffect(() => {
        if (selectedUserId !== null || !users.length) return;
        const loggedInUserInList = users.find((u) => u.userId === loggedInUser?.userId);
        setSelectedUserId(loggedInUserInList?.userId ?? users[0]?.userId ?? null);
    }, [users, selectedUserId, loggedInUser?.userId]);

    const isLoading = isLawFirmLoading || isUsersLoading || !loggedInUser;
    const isNarrow = useMediaQuery(theme.breakpoints.down('md'));
    const currentTabKey = tabOrder[currentTab] ?? tabOrder[0];

    const handleSectionChange = (event: SelectChangeEvent<string>) => {
        navigate(`/settings/${event.target.value}`);
    };

    return (
        <PageWrapper title="Settings">
            <Stack direction={isNarrow ? 'column' : 'row'} spacing={isNarrow ? 2 : 4}>
                {isNarrow ? (
                    <FormControl fullWidth size="small">
                        <InputLabel id="settings-section-select-label">Section</InputLabel>
                        <Select
                            labelId="settings-section-select-label"
                            label="Section"
                            value={currentTabKey}
                            onChange={handleSectionChange}
                        >
                            {tabOrder.map((tabKey) => (
                                <MenuItem key={tabKey} value={tabKey}>
                                    {TAB_LABELS[tabKey]}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                ) : (
                    <Tabs
                        orientation="vertical"
                        variant="standard"
                        value={currentTab}
                        onChange={(_, value) =>
                            navigate(`/settings/${tabOrder[value] ?? tabOrder[0]}`)
                        }
                        sx={{
                            width: '240px',
                            minWidth: '240px',
                            flexShrink: 0,
                            '& .MuiTab-root': {
                                color: theme.palette.neutral.gray,
                                fontSize: '14px',
                                fontWeight: '400',
                                lineHeight: '10px',
                                paddingX: '12px',
                                alignItems: 'flex-start',
                                textAlign: 'left',
                                width: '100%',
                                minWidth: '100%',
                            },
                            '& .MuiTab-root.Mui-selected': {
                                color: theme.palette.primary.main,
                                fontWeight: '600',
                            },
                            '& .MuiTab-wrapper': {
                                alignItems: 'flex-start',
                                width: '100%',
                            },
                        }}
                    >
                        {tabOrder.map((tabKey) => (
                            <Tab
                                key={tabKey}
                                sx={{ textTransform: 'none' }}
                                label={TAB_LABELS[tabKey]}
                            />
                        ))}
                    </Tabs>
                )}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    {isLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', padding: '48px' }}>
                            <CircularProgress size={28} />
                        </Box>
                    ) : (
                        <>
                            <div role="tabpanel" hidden={currentTab !== tabOrder.indexOf('logo')}>
                                {lawFirm && <Logo selectedLawFirm={lawFirm} />}
                            </div>
                            <div
                                role="tabpanel"
                                hidden={currentTab !== tabOrder.indexOf('signature')}
                            >
                                {selectedUserId && (
                                    <Signature
                                        users={users}
                                        selectedUserId={selectedUserId}
                                        signatureLoading={isUserLoading}
                                        signature={selectedUser?.signature ?? ''}
                                        onSelectUser={(id) => setSelectedUserId(id)}
                                    />
                                )}
                            </div>
                            <div
                                role="tabpanel"
                                hidden={currentTab !== tabOrder.indexOf('notification')}
                            >
                                <Notification
                                    users={users}
                                    usersLoading={isUsersLoading}
                                    selectedUserId={selectedUserId}
                                    selectedUser={selectedUser}
                                    onSelectUser={(id) => setSelectedUserId(id)}
                                />
                            </div>
                            <div
                                role="tabpanel"
                                hidden={currentTab !== tabOrder.indexOf('intake-options')}
                            >
                                {lawFirm && <IntakeOptions selectedLawFirm={lawFirm} />}
                            </div>
                            <div
                                role="tabpanel"
                                hidden={currentTab !== tabOrder.indexOf('documents')}
                            >
                                {activeLawFirmId && (
                                    <RetainerAgreement
                                        lawFirmId={activeLawFirmId}
                                        users={users}
                                        isSuperAdmin={isSuperAdmin}
                                    />
                                )}
                            </div>
                            {/* Mounted only while open, so its status and activity log aren't
                                fetched and polled behind the other tabs. Keyed by firm, so a Super
                                Admin switching firms starts on the first page of the log */}
                            {showLexoAccess && lawFirm && currentTabKey === 'lexo-access' && (
                                <div role="tabpanel">
                                    <SupportAccess
                                        key={lawFirm.lawFirmId}
                                        lawFirm={lawFirm}
                                        canManage={isOwnFirmAdmin}
                                    />
                                </div>
                            )}
                            {isSuperAdmin && (
                                <div
                                    role="tabpanel"
                                    hidden={currentTab !== tabOrder.indexOf('lexo-admin')}
                                >
                                    <LexoAdmin />
                                </div>
                            )}
                        </>
                    )}
                </Box>
            </Stack>
        </PageWrapper>
    );
}
